import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  FolderOpen, Plus, Trash2, Share2, Download, Star, Users, 
  BookOpen, Layers, Crown, Search, Grid, List
} from 'lucide-react';
import { toast } from 'sonner';


export default function Collections() {
  const { user } = useOutletContext();
  const { t } = useTranslation();
  const [collections, setCollections] = useState([]);
  const [publicCollections, setPublicCollections] = useState([]);
  const [myProtocols, setMyProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('my-collections');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
    protocols: [],
    is_public: false
  });

  const isPremium = user?.subscription_status && user.subscription_status !== 'free';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [collectionsRes, publicRes, protocolsRes] = await Promise.all([
        fetch(`${API}/collections`, { credentials: 'include' }),
        fetch(`${API}/collections/public/browse`, { credentials: 'include' }),
        fetch(`${API}/protocols`, { credentials: 'include' })
      ]);

      if (collectionsRes.ok) setCollections(await collectionsRes.json());
      if (publicRes.ok) {
        const publicData = await publicRes.json();
        // Backend returns { collections: [...], total, page, pages }
        setPublicCollections(publicData.collections || publicData);
      }
      if (protocolsRes.ok) setMyProtocols(await protocolsRes.json());
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollection.name.trim()) {
      toast.error('Please enter a collection name');
      return;
    }

    if (newCollection.protocols.length === 0) {
      toast.error('Please select at least one protocol');
      return;
    }

    try {
      const response = await fetch(`${API}/collections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newCollection)
      });

      if (response.ok) {
        toast.success('Collection created!');
        setShowCreateDialog(false);
        setNewCollection({ name: '', description: '', protocols: [], is_public: false });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create collection');
      }
    } catch (error) {
      toast.error('Failed to create collection');
    }
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!confirm('Delete this collection?')) return;

    try {
      const response = await fetch(`${API}/collections/${collectionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Collection deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleImportCollection = async (collectionId) => {
    try {
      const response = await fetch(`${API}/collections/${collectionId}/import`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Imported ${result.imported_count} protocols!`);
        fetchData();
      } else {
        toast.error('Failed to import collection');
      }
    } catch (error) {
      toast.error('Failed to import collection');
    }
  };

  const toggleProtocolInCollection = (protocolId) => {
    setNewCollection(prev => ({
      ...prev,
      protocols: prev.protocols.includes(protocolId)
        ? prev.protocols.filter(id => id !== protocolId)
        : [...prev.protocols, protocolId]
    }));
  };

  const filteredCollections = collections.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPublic = publicCollections.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="collections-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2 flex items-center gap-3">
            <FolderOpen className="h-10 w-10 text-primary" />
            Protocol Collections
          </h1>
          <p className="text-muted-foreground">Curate and share bundles of related protocols</p>
        </div>

        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-collection-button">
              <Plus className="h-4 w-4 mr-2" />
              New Collection
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Collection</DialogTitle>
              <DialogDescription>
                Bundle related protocols together for easy sharing
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Collection Name</Label>
                <Input
                  value={newCollection.name}
                  onChange={(e) => setNewCollection({...newCollection, name: e.target.value})}
                  placeholder="e.g., Beginner's Guide, Advanced Fat Loss"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={newCollection.description}
                  onChange={(e) => setNewCollection({...newCollection, description: e.target.value})}
                  placeholder="What is this collection about? Who is it for?"
                  rows={3}
                />
              </div>

              <div>
                <Label className="mb-2 block">Select Protocols ({newCollection.protocols.length} selected)</Label>
                <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-3">
                  {myProtocols.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No protocols available. Create some protocols first!
                    </p>
                  ) : (
                    myProtocols.map(protocol => (
                      <label
                        key={protocol.protocol_id}
                        className="flex items-center gap-3 p-2 rounded hover:bg-secondary/50 cursor-pointer"
                      >
                        <Checkbox
                          checked={newCollection.protocols.includes(protocol.protocol_id)}
                          onCheckedChange={() => toggleProtocolInCollection(protocol.protocol_id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{protocol.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {protocol.peptides?.length || 0} peptides
                          </p>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                <Checkbox
                  checked={newCollection.is_public}
                  onCheckedChange={(checked) => setNewCollection({...newCollection, is_public: checked})}
                />
                <div>
                  <p className="font-medium text-sm">Share publicly</p>
                  <p className="text-xs text-muted-foreground">
                    Allow others to discover and import this collection
                  </p>
                </div>
              </div>

              <Button onClick={handleCreateCollection} className="w-full">
                <FolderOpen className="h-4 w-4 mr-2" />
                Create Collection
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search collections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="my-collections">My Collections</TabsTrigger>
          <TabsTrigger value="browse">Browse Public</TabsTrigger>
        </TabsList>

        <TabsContent value="my-collections">
          {filteredCollections.length === 0 ? (
            <Card className="p-12 text-center glass-card">
              <FolderOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">No Collections Yet</h2>
              <p className="text-muted-foreground mb-6">
                Create collections to organize and share your protocols
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Collection
              </Button>
            </Card>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
              : 'space-y-3'
            }>
              {filteredCollections.map(collection => (
                <CollectionCard
                  key={collection.collection_id}
                  collection={collection}
                  onDelete={handleDeleteCollection}
                  isOwner
                  viewMode={viewMode}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="browse">
          {filteredPublic.length === 0 ? (
            <Card className="p-12 text-center glass-card">
              <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">No Public Collections</h2>
              <p className="text-muted-foreground">
                Be the first to share a collection with the community!
              </p>
            </Card>
          ) : (
            <div className={viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
              : 'space-y-3'
            }>
              {filteredPublic.map(collection => (
                <CollectionCard
                  key={collection.collection_id}
                  collection={collection}
                  onImport={handleImportCollection}
                  viewMode={viewMode}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CollectionCard({ collection, onDelete, onImport, isOwner, viewMode }) {
  if (viewMode === 'list') {
    return (
      <Card className="p-4 glass-card hover:border-primary/50 transition-all">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-primary/10">
            <FolderOpen className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{collection.name}</h3>
            <p className="text-sm text-muted-foreground">
              {collection.protocol_count || collection.protocols?.length || 0} protocols
              {collection.is_public && ' • Public'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!isOwner && (
              <Button size="sm" onClick={() => onImport(collection.collection_id)}>
                <Download className="h-4 w-4 mr-1" />
                Import
              </Button>
            )}
            {isOwner && (
              <Button size="sm" variant="ghost" onClick={() => onDelete(collection.collection_id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5 glass-card hover:border-primary/50 transition-all" data-testid={`collection-${collection.collection_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <FolderOpen className="h-6 w-6 text-primary" />
        </div>
        {collection.is_public && (
          <span className="text-xs px-2 py-1 rounded bg-success/20 text-success">
            Public
          </span>
        )}
      </div>

      <h3 className="font-semibold text-lg mb-1">{collection.name}</h3>
      
      {collection.description && (
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {collection.description}
        </p>
      )}

      <div className="flex items-center gap-3 mb-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <Layers className="h-3 w-3" />
          {collection.protocol_count || collection.protocols?.length || 0} protocols
        </span>
        {collection.download_count > 0 && (
          <span className="flex items-center gap-1">
            <Download className="h-3 w-3" />
            {collection.download_count}
          </span>
        )}
        {collection.author_name && (
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {collection.author_name}
          </span>
        )}
      </div>

      {/* Protocol Preview */}
      {collection.protocol_names && collection.protocol_names.length > 0 && (
        <div className="mb-4 space-y-1">
          {collection.protocol_names.slice(0, 3).map((name, i) => (
            <p key={i} className="text-xs text-muted-foreground">• {name}</p>
          ))}
          {collection.protocol_names.length > 3 && (
            <p className="text-xs text-muted-foreground">
              + {collection.protocol_names.length - 3} more
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        {!isOwner && (
          <Button className="flex-1" onClick={() => onImport(collection.collection_id)}>
            <Download className="h-4 w-4 mr-2" />
            Import All
          </Button>
        )}
        {isOwner && (
          <>
            <Button variant="outline" className="flex-1">
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(collection.collection_id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
